import { memo, useCallback, useState } from 'react';
import { Icon, TextContent } from '@amzn/awsui-components-core';

import styles from './ContentNavigator.module.scss';
import { NavigatorListItem } from './ContentNavigatorViewModel';
import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';
import { useEntityConverter } from '../v2-hooks/useEntityConverter';
import { useGlobalCursor } from '../v2-hooks/useGlobalCursor';
import { navigationLabelForNode } from '../v2-common/ContentNodeExtensions';

interface NavigatorNodeProps {
  readonly item: NavigatorListItem;
  readonly onSelect: (item: NavigatorListItem) => void;
  readonly isPageReorderEnabled: boolean;
  readonly onPageOrderChanged: (parentPageId: string, newOrderIds: readonly string[]) => void;
  readonly sortableArgs?: SortableRenderArgs;
  readonly isShowDragHandlePlaceholder?: boolean;
}

/**
 * Renders a single `NavigatorListItem` as a button, and all its children
 * items as nested buttons. Applies the `selected` class when `item.isSelected`.
 * When the item has children, a collapse/expand triangle is shown to the
 * button's left; clicking the button both navigates and toggles expansion.
 * Expansion state is view-local and defaults to expanded.
 */
const NavigatorNode = memo(function NavigatorNode({
  item,
  onSelect,
  isPageReorderEnabled,
  onPageOrderChanged,
  sortableArgs,
  isShowDragHandlePlaceholder = false,
}: NavigatorNodeProps) {
  const { contentNode, isSelected, hasUnresolvedScanFindings, children } = item;
  const hasChildren = children.length > 0;
  const [isExpanded, setIsExpanded] = useState(true);
  const entityConverter = useEntityConverter();
  useGlobalCursor('grabbing', sortableArgs?.isDragging ?? false);

  const title = navigationLabelForNode(contentNode, entityConverter);
  const buttonClassName = `${styles.nodeButton}${isSelected ? ` ${styles.selected}` : ''}`;
  const listItemClassName = sortableArgs?.isDragging ? styles.draggingNode : undefined;

  const handleSelect = useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <li ref={sortableArgs?.setNodeRef} style={sortableArgs?.style} className={listItemClassName}>
      <div className={styles.nodeRow}>
        {sortableArgs ? (
          <span
            ref={sortableArgs.dragHandleProps.setActivatorNodeRef}
            {...sortableArgs.dragHandleProps.attributes}
            {...sortableArgs.dragHandleProps.listeners}
            className={styles.dragHandle}
            aria-label={`Reorder ${title.trim().length ? title : '<empty>'}`}
          >
            <Icon name="drag-indicator" />
          </span>
        ) : isShowDragHandlePlaceholder ? (
          <span className={styles.dragHandlePlaceholder} aria-hidden="true" />
        ) : null}
        {hasChildren ? (
          <button
            type="button"
            className={styles.triangle}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            onClick={handleToggle}
          >
            {isExpanded ? <Icon name="caret-down-filled" /> : <Icon name="caret-right-filled" />}
          </button>
        ) : null}
        <button
          type="button"
          className={buttonClassName}
          aria-current={isSelected ? 'true' : undefined}
          onClick={handleSelect}
        >
          {/** the button will not be rendered if the title is empty, so fallback to a non-empty string */}
          <span className={styles.nodeTitle}>{title.trim().length ? title : '<empty>'}</span>
          {hasUnresolvedScanFindings ? (
            <span
              className={styles.findingIndicator}
              role="img"
              aria-label="Unresolved scan findings"
            />
          ) : null}
        </button>
      </div>
      {hasChildren && isExpanded ? (
        <ul className={styles.nestedList}>
          <NavigatorSiblingItems
            items={children}
            parentPageId={contentNode.id}
            onSelect={onSelect}
            isPageReorderEnabled={isPageReorderEnabled}
            onPageOrderChanged={onPageOrderChanged}
          />
        </ul>
      ) : null}
    </li>
  );
});

interface NavigatorSiblingItemsProps {
  readonly items: readonly NavigatorListItem[];
  readonly parentPageId: string;
  readonly onSelect: (item: NavigatorListItem) => void;
  readonly isPageReorderEnabled: boolean;
  readonly onPageOrderChanged: (parentPageId: string, newOrderIds: readonly string[]) => void;
}

/**
 * Renders one sibling group, optionally making only that group's pages sortable.
 * Each nested group receives a separate drag context so pages cannot cross hierarchy levels.
 */
export const NavigatorSiblingItems = memo(function NavigatorSiblingItems({
  items,
  parentPageId,
  onSelect,
  isPageReorderEnabled,
  onPageOrderChanged,
}: NavigatorSiblingItemsProps) {
  const keyExtractor = useCallback((item: NavigatorListItem) => item.contentNode.id, []);
  const handleReorder = useCallback(
    (newOrderIds: readonly string[]) => onPageOrderChanged(parentPageId, newOrderIds),
    [onPageOrderChanged, parentPageId],
  );
  const renderSortableItem = useCallback(
    (item: NavigatorListItem, sortableArgs: SortableRenderArgs) => (
      <NavigatorNode
        item={item}
        onSelect={onSelect}
        isPageReorderEnabled={isPageReorderEnabled}
        onPageOrderChanged={onPageOrderChanged}
        sortableArgs={sortableArgs}
      />
    ),
    [isPageReorderEnabled, onPageOrderChanged, onSelect],
  );

  if (isPageReorderEnabled) {
    return (
      <SortableList
        id={`guide-pages-${parentPageId}`}
        items={items}
        keyExtractor={keyExtractor}
        onReorder={handleReorder}
        item={renderSortableItem}
      />
    );
  }

  return (
    <>
      {items.map((item) => (
        <NavigatorNode
          key={item.contentNode.href || item.contentNode.id}
          item={item}
          onSelect={onSelect}
          isPageReorderEnabled={false}
          onPageOrderChanged={onPageOrderChanged}
        />
      ))}
    </>
  );
});

export default NavigatorNode;
